import {ComponentRef, computed, DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, first, forkJoin, map, Observable, of, switchMap, tap} from 'rxjs';
import {ACCOUNT_MODE} from 'src/app/core/entities/account/account-modes/enum/account-mode.enum';
import {ScannerService} from 'src/app/core/services/scanner/scanner.service';
import {INFO_WIDGET_OPTION_TYPE} from 'src/app/pages/service/pages/account-page/enum/info-widget-option.enum';
import {
  IInfoWidgetBaseConfig,
  InfoTypes,
} from 'src/app/pages/service/pages/account-page/interface/info-widget.interface';
import {
  InfoWidgetSelectorModalComponent
} from 'src/app/shared/components/modals/info-widget-selector-modal/info-widget-selector-modal.component';
import {IInfoWidgetSelectorConfig} from 'src/app/shared/interface/info-widget-selector.interface';
import {
  GlobalDynamicFvModalLoaderService
} from 'src/app/shared/services/global-dynamic-fv-modal-loader/global-dynamic-fv-modal-loader.service';
import {GlobalSnackbarService} from 'src/app/shared/services/global-snackbar/global-snackbar.service';
import {ticketEventInUseError, ticketEventQRError,} from '../../ticket-events/config/ticket-event-snackbar.config';
import {
  ITicketEventComplement,
  ITicketEventWallet,
  TicketEvent,
} from '../../ticket-events/interface/ticket-event.model';
import {TicketEventService} from '../../ticket-events/ticket-event.service';
import {formatComplementAsInfoWidget} from '../../ticket-events/utils/ticket-event-complement.processor';
import {AccountModeService} from '../account-modes/account-mode.service';
import {Account} from '../domain/model/account.model';
import {AccountStoreService} from "../application/services/account-store.service";
import {PAYMENT_PERMISSION} from 'src/app/features/permission/domain/enums/payment-permissions.enum';
import {PermissionFacade} from 'src/app/features/permission/application/facades/permission.facade';
import {EVENT_BUS} from '@shared/domain/tokens/event-bus.token';
import {AccountModeChangedEvent} from "@shared/application/events/account/account-mode-changed.event";
import {FV_CHIP_COLORS} from "@discocil/fv-components";
import {ScanWalletFacade} from "@features/wallet/application/facades/wallet-scan-qr.facade";
import {Logger} from "@core/interfaces/Logger";
import {Wallet} from "@features/wallet/domain/model/wallet.model";
import {ScanWalletQRUseCase} from "@features/wallet/application/use-cases/scan-wallet-qr.use-case";

export interface ScanWalletQRResponse {
  ticketEvent: ITicketEventWallet | null;
  wallet: Wallet | null;
}

@Injectable({
  providedIn: 'root',
})
export class AccountQrManagerService {
  private readonly ticketEventService = inject(TicketEventService);
  private readonly globalSnackbarService = inject(GlobalSnackbarService);
  private readonly globalDynamicFvModalLoaderService = inject(
    GlobalDynamicFvModalLoaderService
  );
  private readonly scannerService = inject(ScannerService);
  private readonly accountModeService = inject(AccountModeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountStoreService = inject(AccountStoreService);
  private readonly permissionFacade = inject(PermissionFacade);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly scanWalletFacade = inject(ScanWalletFacade);
  private readonly logger = inject(Logger);

  canPayWithComplement = computed(() => this.permissionFacade.hasPermission(PAYMENT_PERMISSION.canPayWithComplement));
  private readonly scanWalletQRUseCase = inject(ScanWalletQRUseCase);

  constructor() {
    this.initScannedIntegratedQRListener();
  }

  manageAccountQRScan(qrCode: string): void {
    this.ticketEventService
      .getCurrentTicketEvent()
      .pipe(
        first(),
        switchMap((currentTicketEvent) => {
          if (currentTicketEvent) {
            this.globalSnackbarService.open(ticketEventInUseError);

            return of(null);
          }

          return this.getTicketEventWallet(qrCode);
        }),
        switchMap((scanResult) => {
          if (!scanResult) {
            return of(null);
          }

          const {ticketEvent, wallet} = scanResult;
          const entranceEvent = ticketEvent?.ticketEvent ?? null;

          if (!entranceEvent && !wallet) {
            this.globalSnackbarService.open(ticketEventQRError);

            return of(null);
          }

          this.openAccountModeSelector(entranceEvent, wallet);

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getTicketEventWallet(qrCode: string): Observable<ScanWalletQRResponse> {
    return forkJoin({
      wallet: this.scanWalletFacade.execute({qrCode}).pipe(
        catchError(() => of(null))
      ),
      ticketEvent: this.ticketEventService.getTicketEventWalletFromServer(qrCode).pipe(
        catchError(() => of(null))
      ),
    }).pipe(
      map(({ticketEvent, wallet}) => {
        if (ticketEvent || wallet) {
          this.logger.debug('[ScanWalletQR] Wallet data retrieved successfully');

          return {
            ticketEvent,
            wallet,
          };
        } else {
          this.logger.debug('[ScanWalletQR] No wallet data found for QR code');

          return {
            ticketEvent: null,
            wallet: null
          };
        }
      })
    );
  }

  private openAccountModeSelector(
    ticketEvent: TicketEvent | null,
    wallet: Wallet | null
  ): void {
    const options: IInfoWidgetBaseConfig[] = [];

    if (ticketEvent && this.canPayWithComplement()) {
      ticketEvent.complements.forEach((complement: ITicketEventComplement) => {
        const infoWidget: IInfoWidgetBaseConfig =
          formatComplementAsInfoWidget(complement);

        options.push(infoWidget);
      });
    }

    if (wallet) {
      const availableBalance = wallet.availableBalance;
      const walletInfoWidget = {
        id: wallet?.id,
        chips: [
          {
            amount: availableBalance,
            title: '',
            color: FV_CHIP_COLORS.zinc,
          },
        ],
        title: 'Wallet',
        icon: 'far fa-wallet',
        showCheckbox: false,
        showBorder: false,
        isDisabled: availableBalance === 0,
        type: INFO_WIDGET_OPTION_TYPE.wallet,
      };

      options.unshift(walletInfoWidget);
    }

    const modalRef = this.globalDynamicFvModalLoaderService.openModal(
      InfoWidgetSelectorModalComponent,
      {
        config: {
          title: 'info_widget_selector_modal.complements.title',
          options,
          closeOnSelectOption: true,
          showSubmitButton: false,
        } as IInfoWidgetSelectorConfig<IInfoWidgetBaseConfig>,
      }
    );

    this.listenInfoWidgetSelectorSubmit(modalRef, ticketEvent, wallet);
  }

  private listenInfoWidgetSelectorSubmit(
    modalRef: ComponentRef<InfoWidgetSelectorModalComponent>,
    ticketEvent: TicketEvent | null,
    wallet: Wallet | null
  ): void {
    modalRef.instance.submitAction.pipe(
      first(),
      tap(option => {
        if (!option) {
          this.eventBus.publish(new AccountModeChangedEvent(ACCOUNT_MODE.default));
        }

        if (option?.type === INFO_WIDGET_OPTION_TYPE.wallet && wallet) {
          this.eventBus.publish(new AccountModeChangedEvent(ACCOUNT_MODE.wallet));
        }

      }),
      switchMap(option => {
          if (option?.type === INFO_WIDGET_OPTION_TYPE.complement) {
            this.eventBus.publish(new AccountModeChangedEvent(ACCOUNT_MODE.qr_complement));

            return this.handleComplementSelection(ticketEvent, option)
          }

          return of(null);
        }
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private handleComplementSelection(
    ticketEvent: TicketEvent | null,
    optionSelected: InfoTypes
  ): Observable<TicketEvent | null> {
    const selectedComplement = ticketEvent?.complements?.find(
      (complement) => complement._id === optionSelected?.id
    );

    if (ticketEvent) {
      ticketEvent.activeComplement = selectedComplement;

      return this.ticketEventService.insertCurrentTicketEvent(ticketEvent).pipe(
        first(),
        switchMap(() => {
          return this.accountModeService.insertAccountMode(
            ACCOUNT_MODE.qr_complement
          );
        }),
        map(() => ticketEvent)
      );
    }

    return of(null);
  }

  private initScannedIntegratedQRListener(): void {
    this.scannerService.onQRScanned$
      .pipe(
        switchMap((qrCode: string) => {
          return forkJoin({
            accountMode: this.accountModeService.getAccountMode(),
            qrCode: of(qrCode),
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({accountMode, qrCode}) => {
        const currentAccount = this.accountStoreService.getSelectedAccount();

        if (!currentAccount) {
          return;
        }

        if (
          !this.canPayWithComplement() ||
          this.disableIntegratedScanner(currentAccount, accountMode)
        ) {
          return;
        }

        this.manageAccountQRScan(qrCode);
      });
  }

  private disableIntegratedScanner(
    currentAccount: Account | null,
    accountMode: ACCOUNT_MODE | null
  ): boolean {
    if (
      currentAccount &&
      currentAccount.entries &&
      currentAccount.entries.length > 0
    ) {
      return true;
    }

    return accountMode !== (ACCOUNT_MODE.default || ACCOUNT_MODE.initial);
  }

}
